import { useReduxState } from 'hooks';
import { hasAdminAccess } from 'lib/courses';
import { PageInnerContent, PageSidebar } from 'components/page';
import { CoursePage } from 'components/courses/common';

import type { UserCoursePermission } from 'shared-types';
import type { NavLinkOrSubheading } from 'types';

interface Props {
  activeLink: string;
  children: React.ReactNode;
  ensureAdminPermission?: UserCoursePermission;
}

const FollowPage = ({
  activeLink = '',
  children,
  ensureAdminPermission
}: Props) => {
  const {
    messages,
    pageState: { course }
  } = useReduxState();

  const { unreadFeedbackCount, unreviewedAnswerCount } = course || {};

  const sidebarLinks: NavLinkOrSubheading[] = course
    ? [
        ...(hasAdminAccess({ course, permission: 'review-tasks' })
          ? [
              {
                as: `/admin/courses/${course.slug}/follow/users`,
                href: '/admin/courses/[courseSlug]/follow/users',
                icon: 'users' as const,
                title: messages.tasksUsers
              },
              {
                as: `/admin/courses/${course.slug}/follow/tasks`,
                href: '/admin/courses/[courseSlug]/follow/tasks',
                icon: 'list-alt' as const,
                title: messages.tasks,
                unreadItems: unreviewedAnswerCount,
                unreadItemsLabel: `${unreviewedAnswerCount} ${messages.coursesUnreviewedAnswers}`
              },
              {
                as: `/admin/courses/${course.slug}/follow/discussions`,
                href: '/admin/courses/[courseSlug]/follow/discussions',
                customIcon: 'discussion' as const,
                customIconStyle: {
                  fontSize: '1.8rem',
                  marginLeft: -1,
                  marginRight: 5
                },
                title: messages.tasksDiscussions
              }
            ]
          : []),
        ...(hasAdminAccess({ course, permission: 'view-feedback' })
          ? [
              {
                as: `/admin/courses/${course.slug}/follow/feedback`,
                href: '/admin/courses/[courseSlug]/follow/feedback',
                icon: 'bullhorn' as const,
                title: messages.pagesFeedback,
                unreadItems: unreadFeedbackCount,
                unreadItemsLabel: `${unreadFeedbackCount} ${messages.coursesUnread}`
              }
            ]
          : []),
        ...(hasAdminAccess({ course, permission: 'view-analytics' })
          ? [
              {
                as: `/admin/courses/${course.slug}/follow/visitors`,
                href: '/admin/courses/[courseSlug]/follow/visitors',
                icon: 'chart-line' as const,
                title: messages.analyticsVisitorCount
              },
              {
                as: `/admin/courses/${course.slug}/follow/completions`,
                href: '/admin/courses/[courseSlug]/follow/completions',
                icon: 'check-circle' as const,
                title: messages.commonCompletions
              },
              {
                as: `/admin/courses/${course.slug}/follow/chapters`,
                href: '/admin/courses/[courseSlug]/follow/chapters',
                icon: 'chart-bar' as const,
                title: messages.analyticsChapters
              },
              {
                as: `/admin/courses/${course.slug}/follow/videos`,
                href: '/admin/courses/[courseSlug]/follow/videos',
                icon: 'play-circle' as const,
                title: messages.analyticsVideos
              },
              {
                as: `/admin/courses/${course.slug}/follow/search`,
                href: '/admin/courses/[courseSlug]/follow/search',
                icon: 'search' as const,
                title: messages.analyticsSearch
              },
              {
                as: `/admin/courses/${course.slug}/follow/events`,
                href: '/admin/courses/[courseSlug]/follow/events',
                icon: 'list' as const,
                title: messages.commonActivityLog
              }
            ]
          : [])
      ]
    : null;

  return (
    <CoursePage
      activeLink="follow/users"
      course={course}
      ensureAdminPermission={ensureAdminPermission}
      withSidebar={true}
    >
      <PageSidebar
        active={`/admin/courses/${course?.slug}/follow${activeLink}`}
        links={sidebarLinks}
      />
      <PageInnerContent>{children}</PageInnerContent>
    </CoursePage>
  );
};

export default FollowPage;
